import { useRef, useState, type DragEvent } from 'react';
import {
  Upload,
  FileSpreadsheet,
  Download,
  Layers,
  Sparkles,
  ArrowRight,
  Loader2,
  FileCheck,
} from 'lucide-react';
import Button from '@components/ui/Button.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import { cn } from '@utils/Cn.js';
import type { FileIntrospectResult, ImportMappingPreset } from '../types/index.js';

interface FileUploadZoneProps {
  onFileSelected: (file: File) => void;
  isIntrospecting: boolean;
  introspectResult: FileIntrospectResult | null;
  selectedSheet: string;
  onSheetChange: (sheet: string) => void;
  headerRowIndex: number;
  onHeaderRowIndexChange: (idx: number) => void;
  onProceedToMapping: () => void;
  onDownloadTemplate: () => void;
  matchedPreset?: ImportMappingPreset | null;
}

export default function FileUploadZone({
  onFileSelected,
  isIntrospecting,
  introspectResult,
  selectedSheet,
  onSheetChange,
  headerRowIndex,
  onHeaderRowIndexChange,
  onProceedToMapping,
  onDownloadTemplate,
  matchedPreset,
}: FileUploadZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
        }}
      />

      {/* Hero / Information Banner */}
      <div className="rounded-2xl border border-line/60 bg-gradient-to-br from-paper via-surface to-paper/80 p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-ink flex items-center gap-2">
              <Sparkles size={16} className="text-amber-500 shrink-0" />
              Universal Client Spreadsheet Importer
            </h3>
            <p className="text-xs text-ink-faint leading-relaxed max-w-xl">
              Upload any spreadsheet (.xlsx, .xls, .csv) maintained by your client. Our AI &amp; heuristic
              mapping engine automatically aligns columns, cleans dates, and links family accounts with zero manual reformatting.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadTemplate}
            leftIcon={<Download size={13} />}
            className="shrink-0 text-xs font-semibold self-start sm:self-center"
          >
            Sample Template
          </Button>
        </div>
      </div>

      {/* Drag & Drop Surface */}
      <div
        role="button"
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => { setDragOver(false); }}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed p-10 cursor-pointer transition-all text-center select-none focus:outline-none focus:ring-2 focus:ring-slate/30',
          dragOver
            ? 'border-slate bg-slate/5 dark:bg-slate/10 shadow-lg scale-[1.01]'
            : introspectResult
              ? 'border-green-500/30 bg-green-500/5 hover:border-green-500/50'
              : 'border-line-strong/40 hover:border-slate/50 hover:bg-paper/50 bg-surface/40',
        )}
      >
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-200',
            introspectResult
              ? 'bg-green-500/10 text-green-600 dark:text-green-400 scale-105'
              : 'bg-slate/10 dark:bg-slate/20 text-slate',
          )}
        >
          {isIntrospecting ? (
            <Loader2 size={32} className="animate-spin text-slate" />
          ) : introspectResult ? (
            <FileCheck size={32} />
          ) : (
            <FileSpreadsheet size={32} strokeWidth={1.5} />
          )}
        </div>

        <div className="space-y-1">
          {introspectResult ? (
            <>
              <p className="text-sm font-bold text-ink flex items-center justify-center gap-2">
                {introspectResult.fileName}
                <span className="text-xs px-2 py-0.5 rounded-full font-mono bg-green-500/15 text-green-700 dark:text-green-300 font-medium">
                  {introspectResult.totalRows} data rows
                </span>
              </p>
              <p className="text-xs text-ink-faint">
                Click or drop another file to replace
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-ink">
                Click to upload or drag &amp; drop your client spreadsheet
              </p>
              <p className="text-xs text-ink-faint">
                Supports Excel (.xlsx, .xls) and CSV (.csv) files
              </p>
            </>
          )}
        </div>

        {!introspectResult && (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Upload size={14} />}
            disabled={isIntrospecting}
            className="font-bold shadow-xs"
          >
            {isIntrospecting ? 'Analyzing Spreadsheet…' : 'Choose Spreadsheet File'}
          </Button>
        )}
      </div>

      {/* Introspection & Sheet Selector Settings */}
      {introspectResult && (
        <SurfaceCard className="p-6 border border-line/60 space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-line/40 gap-3">
            <div>
              <h4 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
                <Layers size={14} className="text-slate" />
                Sheet &amp; Header Configuration
              </h4>
              <p className="text-xs text-ink-faint mt-0.5">
                Confirm which sheet and row contains your column headers
              </p>
            </div>

            {matchedPreset && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-semibold">
                <Sparkles size={12} />
                Template recognized: {matchedPreset.name}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Sheet selector (if multiple sheets exist) */}
            <div>
              <label className="text-xs font-semibold text-ink-soft block mb-1.5">
                Select Worksheet ({introspectResult.sheets.length})
              </label>
              <select
                value={selectedSheet}
                onChange={(e) => { onSheetChange(e.target.value); }}
                disabled={introspectResult.sheets.length <= 1}
                className="w-full h-10 px-3 rounded-xl border border-line bg-paper text-xs font-medium text-ink focus:outline-none focus:ring-2 focus:ring-slate/20 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {introspectResult.sheets.map((sheet) => (
                  <option key={sheet} value={sheet}>
                    {sheet}
                  </option>
                ))}
              </select>
            </div>

            {/* Header row selector */}
            <div>
              <label className="text-xs font-semibold text-ink-soft block mb-1.5">
                Header Row Position
              </label>
              <select
                value={headerRowIndex}
                onChange={(e) => { onHeaderRowIndexChange(parseInt(e.target.value, 10)); }}
                className="w-full h-10 px-3 rounded-xl border border-line bg-paper text-xs font-medium text-ink focus:outline-none focus:ring-2 focus:ring-slate/20 transition-all"
              >
                {[0, 1, 2, 3, 4].map((idx) => (
                  <option key={idx} value={idx}>
                    Row {idx + 1} {idx === introspectResult.detectedHeaderRowIndex ? '(Auto-Detected Header)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick Preview of Detected Columns */}
          <div className="rounded-xl border border-line/40 bg-surface/40 p-3 space-y-2">
            <p className="text-[11px] font-bold text-ink-faint uppercase tracking-wide">
              Detected Columns ({introspectResult.headers.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {introspectResult.headers.map((h, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-mono font-medium bg-paper border border-line/60 text-ink"
                >
                  {h}
                </span>
              ))}
            </div>
          </div>

          {/* CTA Proceed Button */}
          <div className="flex justify-end pt-2">
            <Button
              variant="primary"
              size="md"
              onClick={onProceedToMapping}
              rightIcon={<ArrowRight size={15} />}
              className="w-full sm:w-auto font-bold shadow-sm"
            >
              Continue to Column Mapping
            </Button>
          </div>
        </SurfaceCard>
      )}
    </div>
  );
}
